import React, { useRef, useState, useMemo, useLayoutEffect } from 'react';
import { scaleLinear } from '@visx/scale';
import {
  width as checkWidth,
  height as checkHeight,
  svgPathData as checkSvgPathData,
} from '@fortawesome/pro-solid-svg-icons/faCheck';

import Lollipop from 'js/components/Charts/Lollipop';
import { scaleColorTraffic, standardColor } from 'js/utils/color';
import {
  TILE_PRESENTATION,
  getNormalizedQuestionScore,
  liveTilePresentation,
} from '../../../utilities';
import { sentimentColor } from 'js/utils/sentiment-question';

function CompletedCheck({ size }) {
  const scale = size / Math.max(checkHeight, checkWidth);
  const iconWidth = checkWidth * scale;
  const iconHeight = checkHeight * scale;

  // Origin is the same point as a no-response lollipop pop (normalized 0.5).
  return (
    <g
      className="completed-marker"
      transform={`translate(${-iconWidth / 2}, ${
        -iconHeight / 2
      }) scale(${scale})`}
    >
      <path d={checkSvgPathData} fill={standardColor()} />
    </g>
  );
}

/**
 * Prefer a check twice the lollipop, but shrink when columns get tight so
 * neighboring marks keep a gap. Stop at the lollipop width: if columns are
 * narrower than that, checks overlap the same way lollipops would. A denser
 * packed layout is a later, separate change.
 */
function completedCheckSize(columnWidth, popWidth) {
  const preferred = popWidth * 2;
  const minSize = popWidth;
  const minGap = 2;
  const maxThatFits = Math.max(minSize, columnWidth - minGap);

  return Math.min(preferred, maxThatFits);
}

/**
 * The gradient runs bottom (worst) to top (best) in normalized space, so it is
 * the same ramp regardless of how many options the questions have.
 */
const DEFAULT_GRADIENT_STOPS = [...scaleColorTraffic.range()]
  .reverse()
  .map((color, i, all) => [1 - i / (all.length - 1), color]);

const AssessmentDetailChart = ({ assessment, height, gradientStops }) => {
  const popWidth = 10;
  const verticalMargin = popWidth / 2;
  const chartHeight = height - popWidth;

  const gradientProps = useMemo(
    () => ({ stops: gradientStops }),
    [gradientStops]
  );

  const chartRef = useRef();
  const [width, setWidth] = useState(null);

  useLayoutEffect(() => {
    const handleResize = () => {
      setWidth(chartRef.current ? chartRef.current.offsetWidth : null);
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!assessment || !assessment.id) {
    return null;
  }

  const questionCount = assessment.question_sets.reduce(
    (acc, qs) => acc + qs.questions.length,
    0
  );
  const questionWidth = width / Math.max(questionCount, 1);
  const checkSize = completedCheckSize(questionWidth, popWidth);

  const counts = {};
  assessment.question_sets.forEach((qs) => {
    qs.questions.forEach((q) => {
      q.responses.forEach((r) => {
        const key = `${q.id}-${r.value}`;
        counts[key] = counts.hasOwnProperty(key) ? counts[key] + 1 : 1;
      });
    });
  });

  // Scores are normalized to 0..1 before they reach the chart, so the axis is
  // fixed rather than derived from any one question's values.
  const yScale = scaleLinear({ domain: [0, 1], range: [0, chartHeight] });

  const maxStopIndex = gradientProps.stops.length - 1;
  return (
    <div className="live-assessment-detail-chart" ref={chartRef}>
      <svg viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient
            id="assessment-detail-line-chart-gradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            {gradientProps.stops.map((stop, i) => (
              <stop
                key={i}
                offset={`${(100 * i) / maxStopIndex}%`}
                style={{ stopColor: stop[1], stopOpacity: 1 }}
              />
            ))}
          </linearGradient>
        </defs>
        <g className="chart-axes" transform={`translate(0,${verticalMargin})`}>
          <line
            x1={0}
            y1={chartHeight - 0.75}
            x2={width}
            y2={chartHeight - 0.75}
            className="chart-axis"
            strokeWidth="1.5px"
          />
          <rect
            x={0}
            y={0}
            height={chartHeight}
            width={2}
            className="color-scale"
            fill="url(#assessment-detail-line-chart-gradient)"
          />
        </g>
        <g transform={`translate(0,${-verticalMargin})`}>
          {assessment.question_sets.map((qs, qsi) => (
            <g
              key={qs.id}
              transform={`translate(${
                qsi * qs.questions.length * questionWidth
              })`}
            >
              {qs.questions.map((q, qi) => {
                const responses = (q.responses ?? []).filter((r) => !r.is_na);
                const presentation = liveTilePresentation(q);
                const score = getNormalizedQuestionScore(q);
                const columnX = qi * questionWidth;

                if (responses.length === 0) {
                  return (
                    <g key={q.id} transform={`translate(${columnX}, 0)`}>
                      <g
                        transform={`translate(${
                          (questionWidth - popWidth) / 2
                        }, ${height - chartHeight / 2})`}
                      >
                        <Lollipop
                          height={chartHeight / 2}
                          width={popWidth}
                          className="no-response-marker"
                        />
                      </g>
                    </g>
                  );
                }

                if (
                  presentation !== TILE_PRESENTATION.SCORED_DISTRIBUTION ||
                  score == null
                ) {
                  return (
                    <g key={q.id} transform={`translate(${columnX}, 0)`}>
                      <g
                        transform={`translate(${questionWidth / 2}, ${
                          height - chartHeight / 2
                        })`}
                      >
                        <CompletedCheck size={checkSize} />
                      </g>
                    </g>
                  );
                }

                const barHeight = yScale(score);

                return (
                  <g key={q.id} transform={`translate(${columnX}, 0)`}>
                    <g
                      transform={`translate(${
                        (questionWidth - popWidth) / 2
                      }, ${height - barHeight})`}
                    >
                      <Lollipop
                        height={barHeight}
                        width={popWidth}
                        decorator={() => (
                          <line
                            x1={-popWidth / 2}
                            y1={barHeight - 0.75}
                            x2={popWidth / 2}
                            y2={barHeight - 0.75}
                            className="chart-axis"
                          />
                        )}
                        fill={sentimentColor(score)}
                      />
                    </g>
                  </g>
                );
              })}
            </g>
          ))}
        </g>
      </svg>
      <div>
        <ol className="question-labels">
          {assessment.question_sets.flatMap((qs) =>
            qs.questions.map((q) => (
              <li key={q.key} className="question-label">
                {q.text}
              </li>
            ))
          )}
        </ol>
      </div>
    </div>
  );
};

AssessmentDetailChart.defaultProps = {
  gradientStops: DEFAULT_GRADIENT_STOPS,
};

export const Glyph = (
  <svg
    viewBox="0 0 32 32"
    className="chart-glyph live-assessment-lollipop-glyph"
  >
    <defs>
      <mask
        id="live-assessment-lollipop-glyph-head"
        maskContentUnits="objectBoundingBox"
      >
        <rect width="1" height="1" fill="white" />
        <circle cx="0.5" cy="0.5" r="0.25" fill="black" />
      </mask>
    </defs>
    <line x1="2" y1="30" x2="30" y2="30" className="chart-axis" />
    <g>
      <rect
        x="8.5"
        y="19.5"
        width="3"
        height="10.5"
        className="lolli lolli-low"
      />
      <circle
        cx="10"
        cy="17"
        r="5"
        className="lolli lolli-low"
        mask="url(#live-assessment-lollipop-glyph-head)"
      />
    </g>
    <g>
      <rect
        x="20.5"
        y="10.5"
        width="3"
        height="19.5"
        className="lolli lolli-high"
      />
      <circle
        cx="22"
        cy="8"
        r="5"
        className="lolli lolli-high"
        mask="url(#live-assessment-lollipop-glyph-head)"
      />
    </g>
  </svg>
);

export default AssessmentDetailChart;
